import React, { useCallback, useMemo } from "react";
import { z } from "zod";

import {
	FormDatePicker,
	FormField,
	FormMultipleSelect,
	FormSingleSelect,
	PanelFilters,
} from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../components/Inputs/SelectEmployee";
import type { EmployeeValue } from "../../../components/Inputs/SelectEmployee";

import { fetchHashtags } from "../../../api/services/defs/hashtags";

export const SHARED_WITH_OPTIONS = [
	{ label: "Everyone", value: "everyone" },
	{ label: "Subject", value: "subject" },
	{ label: "Subject's Manager", value: "subject-manager" },
	{ label: "Subject and Subject's Manager", value: "subject-+-manager" },
];

export const RESPONSE_TO_REQUEST_OPTIONS = [
	{ label: "Yes", value: "true" },
	{ label: "No", value: "false" },
];

const CompanyFeedbackFilterSchema = z
	.object({
		subject: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
		author: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
		hashTags: z.array(z.string()).default([]),
		sharedWith: z.string().nullable().optional(),
		responseToRequest: z.string().nullable().optional(),
		dateAfter: z.date().nullable().optional(),
		dateBefore: z.date().nullable().optional(),
	})
	.refine((data) => !data.dateAfter || !data.dateBefore || data.dateAfter <= data.dateBefore, {
		message: "Must be after Date After",
		path: ["dateBefore"],
	});

export type CompanyFeedbackFilter = z.infer<typeof CompanyFeedbackFilterSchema>;

type CompanyFeedbackFiltersProps = {
	isOpen: boolean;
	onClose: () => void;
	filters: Partial<CompanyFeedbackFilter>;
	onChange: (filters: Partial<CompanyFeedbackFilter>) => void;
};

const CompanyFeedbackFilters = ({ isOpen, onClose, filters, onChange }: CompanyFeedbackFiltersProps) => {
	const fetchHashTagOptions = useCallback(async (search: string) => {
		try {
			const hashtags = await fetchHashtags();
			const filtered = search
				? hashtags.filter((tag) => tag.toLowerCase().includes(search.toLowerCase()))
				: hashtags;
			return filtered.map((tag) => ({ label: tag, value: tag }));
		} catch {
			return [];
		}
	}, []);
	const defaultValues = useMemo<CompanyFeedbackFilter>(
		() => ({
			subject: filters.subject ?? null,
			author: filters.author ?? null,
			hashTags: filters.hashTags || [],
			sharedWith: filters.sharedWith,
			responseToRequest: filters.responseToRequest,
			dateAfter: filters.dateAfter ?? null,
			dateBefore: filters.dateBefore ?? null,
		}),
		[
			filters.subject,
			filters.author,
			filters.hashTags,
			filters.sharedWith,
			filters.responseToRequest,
			filters.dateAfter,
			filters.dateBefore,
		]
	);

	const handleSubmit = ({
		value,
	}: {
		value: Partial<CompanyFeedbackFilter>;
		resetForm: () => void;
		setError: (field: keyof CompanyFeedbackFilter, error: string) => void;
	}) => {
		const appliedFilters: Partial<CompanyFeedbackFilter> = { ...value };
		Object.keys(appliedFilters).forEach((key) => {
			const fieldValue = appliedFilters[key];
			if (
				fieldValue === null ||
				fieldValue === undefined ||
				(Array.isArray(fieldValue) && fieldValue.length === 0)
			) {
				delete appliedFilters[key];
			}
		});
		onChange(appliedFilters);
		onClose();
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={CompanyFeedbackFilterSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		>
			<div className="space-y-4">
				<FormField<EmployeeValue | null>
					id="subject"
					label="Subject"
					input={(field) => (
						<SingleSelectEmployee
							value={field.value ?? undefined}
							onChange={(id, name) => {
								if (id && name) {
									field.onChange({ id, name });
								} else {
									field.onChange(null);
								}
							}}
							placeholder="Search for a subject..."
							isClearable
						/>
					)}
				/>
				<FormField<EmployeeValue | null>
					id="author"
					label="Author"
					input={(field) => (
						<SingleSelectEmployee
							value={field.value ?? undefined}
							onChange={(id, name) => {
								if (id && name) {
									field.onChange({ id, name });
								} else {
									field.onChange(null);
								}
							}}
							placeholder="Search for an author..."
							isClearable
						/>
					)}
				/>

				<FormMultipleSelect<string>
					id="hashTags"
					label="Hashtags"
					fetchOptions={fetchHashTagOptions}
					selectionVariant="combobox"
					placeholder="Search hashtags..."
				/>

				<FormSingleSelect<string>
					id="sharedWith"
					label="Shared With"
					options={SHARED_WITH_OPTIONS}
					selectionVariant="select"
					placeholder="Select..."
					isClearable
				/>

				<FormSingleSelect<string>
					id="responseToRequest"
					label="Was Requested"
					options={RESPONSE_TO_REQUEST_OPTIONS}
					selectionVariant="select"
					placeholder="Select..."
					isClearable
				/>

				<FormDatePicker id="dateAfter" label="Date After" clearable />

				<FormDatePicker id="dateBefore" label="Date Before" clearable />
			</div>
		</PanelFilters>
	);
};

export default CompanyFeedbackFilters;
